import { TemplateElement, html, defineElement, when, unsafeHTML } from '@webtides/element-js';
import style from './story-benefit.css';
import { responsiveImage } from '../../../../directives/images.js';
import { backgroundVideo } from '../../../../directives/video.js';
import '../rive-animation/rive-animation.js';
import '../../../icon/icon.js';

export default class StoryBenefit extends TemplateElement {
    constructor() {
        super({ styles: [style] });
    }

    properties() {
        return {
            text: '',
            image: undefined,
            video: '',
            riveAnimation: undefined,
            notification: null,
            link: null,
        };
    }

    template() {
        const notification = this.notification;
        const hasNotification = Boolean(notification?.icon || notification?.text || notification?.link?.url);

        return html`
            <div class="flex flex-col md:flex-row items-stretch md:items-center w-full gap-56 md:gap-40 lg:gap-48">
                <div class="relative shrink-0 w-full md:w-[224px] lg:w-[310px] xl:w-[340px]">
                    <div
                        class="story-benefit-media relative w-full aspect-square overflow-hidden rounded-[var(--mdx-sys-border-radius-md)] shadow-[0_0_15px_0_rgba(0,0,0,0.25)] md:h-[305px] md:aspect-auto lg:h-[388px] lg:rounded-[var(--mdx-sys-border-radius-lg)] lg:shadow-[0_0_20px_0_rgba(0,0,0,0.25)] xl:h-[425px]"
                    >
                        ${when(
                            this.riveAnimation?.src,
                            html`
                                <mb-rive-animation
                                    class="block w-full h-full object-cover"
                                    src="${this.riveAnimation?.src}"
                                    artboard="${this.riveAnimation?.artboard || ''}"
                                    state-machine="${this.riveAnimation?.stateMachine || ''}"
                                    fit="${this.riveAnimation?.fit || 'contain'}"
                                    alignment="${this.riveAnimation?.alignment || 'center'}"
                                    trigger="${this.riveAnimation?.trigger || 'onView'}"
                                    pause-out-of-view="${this.riveAnimation?.pauseOutOfView ?? true}"
                                ></mb-rive-animation>
                            `,
                            when(
                                this.video,
                                backgroundVideo(this.video, {
                                    poster: this.image?.src,
                                    cssClass: 'block w-full h-full object-cover',
                                }),
                                when(
                                    this.image?.srcset,
                                    responsiveImage(
                                        this.image,
                                        'story-benefit-image block w-full h-full object-cover',
                                        false,
                                        'auto',
                                        '(min-width: 1280px) 340px, (min-width: 1024px) 310px, (min-width: 768px) 224px, calc(100vw - 32px)',
                                    ),
                                ),
                            ),
                        )}
                    </div>
                    ${when(
                        hasNotification,
                        html`
                            <div
                                class="story-benefit-notification absolute z-[2] flex items-start gap-16 p-16 rounded-[var(--mdx-sys-border-radius-default)] bg-[var(--mdx-sys-color-primary-subtle1)] text-[var(--mdx-sys-color-primary-default)] left-8 right-8 -bottom-24 md:right-auto md:left-[65px] md:-bottom-32 md:w-[398px] lg:left-[112px] lg:-bottom-12 lg:w-[491px] xl:left-[144px]"
                            >
                                ${when(
                                    notification?.icon,
                                    html`<mb-icon name="${notification?.icon}" class="shrink-0 size-24"></mb-icon>`,
                                )}
                                <div class="min-w-0 text-sm font-normal leading-[1.5] lg:text-base lg:font-medium">
                                    ${when(
                                        notification?.text,
                                        html`<div class="story-benefit-notification-text">
                                            ${unsafeHTML(notification?.text)}
                                        </div>`,
                                    )}
                                    ${when(
                                        notification?.link?.url,
                                        html`
                                            <a
                                                href="${notification?.link?.url}"
                                                class="underline underline-offset-2"
                                                element="story benefit"
                                                tracking-label="${notification?.link?.trackingLabel || ''}"
                                                >${notification?.link?.text}</a
                                            >
                                        `,
                                    )}
                                </div>
                            </div>
                        `,
                    )}
                </div>

                <div
                    class="flex flex-1 min-w-0 flex-col gap-8 text-sm font-medium leading-[1.5] md:font-normal lg:font-medium xl:text-base"
                >
                    ${when(
                        this.text,
                        html`
                            <div class="richtext ${'[&>p]:!fix-body2-medium'} story-benefit-text max-w-none m-0">
                                ${unsafeHTML(this.text)}
                            </div>
                        `,
                    )}
                    ${when(
                        this.link?.url,
                        html`
                            <a
                                class="story-benefit-link w-max max-w-full font-normal underline underline-offset-2"
                                href="${this.link.url}"
                                element="story benefit"
                                tracking-label="${this.link?.trackingLabel || ''}"
                            >
                                ${this.link.text}
                            </a>
                        `,
                    )}
                </div>
            </div>
        `;
    }
}

defineElement('mb-story-benefit', StoryBenefit);
